import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from '../../../core/api/client'
import { ApiError, type Categoria } from '../../../core/api/types'
import type { OrigemDaCarga } from './programacaoLogica'

export interface LocalRef { uuid: string; name: string }

export type StatusDaProgramacao = 'previa' | 'confirmada'
export type TipoDoEnvio = 'criacao' | 'reprogramacao' | 'confirmacao' | 'ajuste_equipe'

/** A carga como o portal vê: o número pelo horário, e quem pediu. */
export interface CargaProgramada {
  uuid: string
  numero: number
  horario: string
  origem: OrigemDaCarga
}

export interface ContratoDaProgramacao {
  uuid: string
  numero: string
  nome: string
  produtor: string | null
  fazenda: LocalRef | null
  destino: LocalRef | null
}

/** O ajuste da equipe como o cliente vê: o que entrou, o que saiu e quando. */
export interface AjusteDaEquipe {
  em: string | null
  entraram: string[]
  sairam: string[]
  horarios_alterados: number
}

/** As placas que a equipe liberou ao cliente: a foto do momento da liberação. */
export interface PlacaLiberada {
  numero: number
  horario: string
  cavalo: string | null
  carretas: string[]
  motorista: string | null
  liberada_em: string | null
  /** O tipo de cada veículo, que desenha o ícone da frota. */
  cavalo_categoria?: Categoria | null
  carretas_categorias?: (Categoria | null)[]
}

/** Só a solicitação e as placas liberadas: o portal não recebe andamento nem a
 *  escala ao vivo (Samuel, 03/11/2026). */
export interface Programacao {
  uuid: string
  data: string
  status: StatusDaProgramacao
  versao: number
  contrato: ContratoDaProgramacao
  cargas: CargaProgramada[]
  envios: { tipo: TipoDoEnvio; origem: OrigemDaCarga; em: string | null }[]
  ajustes_da_equipe: AjusteDaEquipe[]
  liberadas: PlacaLiberada[]
  criada_em: string | null
  confirmada_em: string | null
  atualizada_em: string | null
}

export interface ResumoDoAviso {
  programacao_uuid: string
  data: string | null
  status?: StatusDaProgramacao
  cliente: string | null
  contrato: { uuid: string; numero: string; produtor: string | null; fazenda: string | null; destino: string | null }
  antes: (string | null)[] | null
  depois: (string | null)[]
  extras?: number
  reducoes?: number
  quem: string
}

export type TipoDoAviso = 'nova' | 'reprogramacao' | 'confirmacao'

export interface AvisoDeProgramacao {
  uuid: string
  tipo: TipoDoAviso
  programacao_uuid: string | null
  resumo: ResumoDoAviso
  dupla: string | null
  criado_em: string | null
}

const CHAVE = ['programacoes'] as const

export function useProgramacoesDoDia(data: string) {
  return useQuery({
    queryKey: [...CHAVE, 'dia', data],
    queryFn: () => http.get<Programacao[]>('/programacoes/', { params: { data } }),
    enabled: Boolean(data),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  })
}

export function useResumoDosDias(de: string, ate: string) {
  return useQuery({
    queryKey: [...CHAVE, 'resumo', de, ate],
    queryFn: () => http.get<Record<string, number>>('/programacoes/resumo', { params: { de, ate } }),
    enabled: Boolean(de && ate),
  })
}

export function useProgramacao(uuid: string | undefined) {
  return useQuery({
    queryKey: [...CHAVE, 'detalhe', uuid],
    queryFn: () => http.get<Programacao>(`/programacoes/${uuid}`),
    enabled: Boolean(uuid),
  })
}

export interface CorpoDaNova {
  contrato_uuid: string
  data: string
  status: StatusDaProgramacao
  horarios: string[]
}

export function useCriarProgramacao() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CorpoDaNova) => http.post<Programacao>('/programacoes/', body),
    onSettled: () => qc.invalidateQueries({ queryKey: CHAVE }),
  })
}

export interface CorpoDaReprogramacao {
  uuid: string
  cargas: { uuid: string | null; horario: string }[]
  versao: number
  confirmar: boolean
}

export function useReprogramar() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, ...body }: CorpoDaReprogramacao) => http.put<Programacao>(`/programacoes/${uuid}`, body),
    onSettled: () => qc.invalidateQueries({ queryKey: CHAVE }),
  })
}

/** O 409 da segunda programação no mesmo dia traz o identificador da existente. */
export function programacaoExistente(err: unknown): string | null {
  if (err instanceof ApiError && (err.code as string) === 'PROGRAMACAO_JA_EXISTE') {
    const det = err.details as { programacao_uuid?: string } | undefined
    return det?.programacao_uuid ?? null
  }
  return null
}

/** A programação mudou enquanto a pessoa editava: o cliente e a equipe ao mesmo tempo. */
export function programacaoMudou(err: unknown): boolean {
  return err instanceof ApiError && (err.code as string) === 'PROGRAMACAO_MUDOU'
}

// ── Aviso da equipe ──────────────────────────────────────────────────────────

export function useAvisosPendentes(ativo: boolean) {
  return useQuery({
    queryKey: [...CHAVE, 'avisos'],
    queryFn: () => http.get<AvisoDeProgramacao[]>('/programacoes/avisos/pendentes'),
    enabled: ativo,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  })
}

export function useDarCiente() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => http.post<AvisoDeProgramacao>(`/programacoes/avisos/${uuid}/ciente`),
    onSettled: () => qc.invalidateQueries({ queryKey: [...CHAVE, 'avisos'] }),
  })
}
